import { useCallback, useEffect, useRef, useState } from "react";

import { Balance, DashboardBalances, requestDashboardBalances } from "../Api/dashboardBalances";

const BALANCE_REFRESH_INTERVAL = 5 * 60 * 1000;
const initialBalance: Balance = { amount: null, status: "loading" };

// Share in-flight work across StrictMode effect replays and concurrent consumers.
// Each effect still owns its subscription; only the request timeout aborts fetch.
let inFlight: Promise<DashboardBalances> | undefined;

function fetchDashboardBalances(): Promise<DashboardBalances> {
  if (inFlight) return inFlight;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20000);
  inFlight = requestDashboardBalances(controller.signal).finally(() => {
    window.clearTimeout(timeout);
    inFlight = undefined;
  });
  return inFlight;
}

export function formatDashboardBalance(balance: Balance) {
  if (balance.status === "missing_key") return "키 미설정";
  if (balance.status === "error") return "조회 실패";
  if (balance.status === "loading" || balance.amount === null) return "—";
  return `$${balance.amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function useDashboardBalances() {
  const [balances, setBalances] = useState<DashboardBalances>({
    grizzly: initialBalance,
    onlinesim: initialBalance,
  });

  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  const refresh = useCallback(() => refreshRef.current(), []);

  useEffect(() => {
    let disposed = false;
    let pending = false;

    const refresh = async () => {
      if (disposed || pending) return;
      pending = true;
      setIsRefreshing(true);
      try {
        const data = await fetchDashboardBalances();
        if (!disposed) setBalances(data);
      } catch {
        if (!disposed) {
          const failed: Balance = { amount: null, status: "error" };
          setBalances({ grizzly: failed, onlinesim: failed });
        }
      } finally {
        pending = false;
        if (!disposed) setIsRefreshing(false);
      }
    };

    refreshRef.current = refresh;
    refresh();
    const interval = window.setInterval(refresh, BALANCE_REFRESH_INTERVAL);
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, []);

  return { balances, isRefreshing, refresh };
}
